// The sound bank: every effect designed once here, rendered offline into a buffer the first time the audio starts,
// then loudness-matched so a shield and a crash sit where the mix wants them whatever their synthesis. Playing a
// sound is then one buffer and two gains: cheap enough for a 50-rider race, and heavier synthesis costs nothing live.
//
// One sonic identity: every sound is built in layers (a transient that gives it an edge, a body that gives it weight,
// a tail that places it in a space), the musical cues ring real bells and plucked strings in D major, the key the
// soundtrack (music/songs.ts) is written in, and anything from the race world moves across the stereo field.
import { tone, fm, noise, thump, click, metal, saturate, note, bell, pluck, supersaw, COWBELL, type Kit } from './synth.js';

export type BusName = 'sfx' | 'ui' | 'amb';

export interface SoundDef {
  /** Rendered length (s), tail included. */
  len: number;
  /** Target short-term loudness (dBFS, loudest 50 ms window) after normalisation. */
  level: number;
  /** Bus it plays on by default. */
  bus: BusName;
  /** Reverb send (0: dry). */
  verb: number;
  /** How far the riding sounds and the music dip under it (0: not at all). */
  duck?: number;
  /** Random pitch spread per play (fraction): the same hit twice in a row should not sound like a loop. */
  vary: number;
  draw: (k: Kit) => void;
}

const [D5, Fs5, A5, B5, D6, E6, Fs6, A6, D7] = ['D5', 'F#5', 'A5', 'B5', 'D6', 'E6', 'F#6', 'A6', 'D7'].map(note);
const [D3, A4, Fs4, D4, B4] = ['D3', 'A4', 'F#4', 'D4', 'B4'].map(note);

/** A struck bell of the game's set: real partials, a clapper, a little beating. */
const ring = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0, bright = 0.6) => bell(k, { at, dur, vol, f, pan, bright });
/** A small glass: the light cue of the interface and the items. */
const glass = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0) => bell(k, { at, dur, vol, f, pan, kind: 'glass', bright: 0.7 });
/** A plucked string, bright and quick: runs and fanfares. */
const string = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0, bright = 0.7) => pluck(k, { at, dur, vol, f, pan, bright });
/** Air: a brush of bright noise across both sides. */
const sparkle = (k: Kit, at: number, dur: number, vol: number) => {
  noise(k, { at, dur, vol, filter: 'highpass', f: 7000, attack: 0.03, pan: -0.4 });
  noise(k, { at: at + 0.03, dur, vol, filter: 'highpass', f: 8000, attack: 0.03, pan: 0.4 });
};
/** A whoosh that passes by: a band of air sweeping in pitch and across the field, with a hiss on top. */
const whoosh = (k: Kit, at: number, dur: number, vol: number, from: number, to: number, pan: number, panTo: number) => {
  noise(k, { at, dur, vol, filter: 'bandpass', f: from, to, q: 1.3, attack: dur * 0.12, pan, panTo });
  noise(k, { at: at + dur * 0.1, dur: dur * 0.8, vol: vol * 0.35, filter: 'highpass', f: Math.max(from, to) * 0.8, attack: dur * 0.2, pan: pan * 0.5, panTo: panTo * 0.5 });
};
/** A crowd swell: a chest of low voices and three vowel-like bands across the stereo field, rising together. */
const roar = (k: Kit, at: number, dur: number, vol: number, top: number) => {
  noise(k, { at, dur, vol: vol * 0.7, filter: 'bandpass', f: 220, to: 300, q: 0.9, attack: dur * 0.35 });
  noise(k, { at, dur, vol, filter: 'bandpass', f: 500, to: 700, q: 1.2, attack: dur * 0.3, pan: -0.5 });
  noise(k, { at: at + 0.02, dur, vol: vol * 0.9, filter: 'bandpass', f: 1000, to: top, q: 1.3, attack: dur * 0.3, pan: 0.5 });
  noise(k, { at: at + 0.04, dur: dur * 0.8, vol: vol * 0.5, filter: 'bandpass', f: 2400, to: top * 1.4, q: 1.6, attack: dur * 0.25 });
};
/** A hand clap: three micro-bursts a few milliseconds apart, then a short room tail. */
const clap = (k: Kit, at: number, vol: number, pan = 0) => {
  for (const [d, v] of [[0, 1], [0.009, 0.8], [0.019, 0.9]]) noise(k, { at: at + d, dur: 0.012, vol: vol * v, filter: 'bandpass', f: 1300, q: 1.4, attack: 0.0006, pan });
  noise(k, { at: at + 0.026, dur: 0.11, vol: vol * 0.55, filter: 'bandpass', f: 1100, q: 0.9, attack: 0.001, pan });
};
/** Small parts of a bike hitting the road: a few tiny metal taps, scattered. */
const tinkle = (k: Kit, at: number, vol: number) => {
  for (const [d, f, p] of [[0, 2400, -0.4], [0.08, 3100, 0.5], [0.19, 2700, 0.1], [0.27, 3600, -0.2]]) metal(k, at + d, f, 0.12, vol, p);
};
/** A soft wooden tap: the interface touched, not beeped at. */
const tap = (k: Kit, at: number, f: number, vol: number) => {
  noise(k, { at, dur: 0.03, vol: vol * 0.8, filter: 'bandpass', f: f * 3, q: 3, attack: 0.0006 });
  tone(k, { at, dur: 0.07, vol, f, to: f * 0.8, type: 'sine', attack: 0.001 });
};

export const SOUNDS: Record<string, SoundDef> = {
  // ---- start: three bells, then the gun ----
  beep: { len: 0.7, level: -17, bus: 'sfx', verb: 0.6, vary: 0, draw: k => { ring(k, 0, A5, 0.55, 0.5, 0, 0.5); click(k, 0, 3000, 0.05); } },
  go: {
    len: 1.9, level: -12, bus: 'sfx', verb: 0.8, duck: 0.5, vary: 0, draw: k => {
      [D6, Fs6, A6].forEach((f, i) => ring(k, 0, f, 1.4, 0.3, (i - 1) * 0.4, 0.8));
      thump(k, 0, 120, 40, 0.4, 0.6);
      noise(k, { at: 0, dur: 0.45, vol: 0.14, filter: 'highpass', f: 1800, to: 7000, attack: 0.002, pan: -0.6, panTo: 0.6 });
      sparkle(k, 0.05, 0.9, 0.05);
      roar(k, 0.1, 1.2, 0.05, 1800);
    },
  },
  // ---- items ----
  box: { len: 0.7, level: -19, bus: 'sfx', verb: 0.9, vary: 0.02, draw: k => { [D6, Fs6, A6, D7].forEach((f, i) => glass(k, i * 0.035, f, 0.35, 0.2, (i - 1.5) * 0.3)); sparkle(k, 0.05, 0.3, 0.02); } },
  item: { len: 1.1, level: -16, bus: 'sfx', verb: 1, vary: 0.01, draw: k => { ring(k, 0, A5, 0.35, 0.3, -0.15); ring(k, 0.09, D6, 0.8, 0.35, 0.15, 0.7); glass(k, 0.09, A6, 0.5, 0.08); sparkle(k, 0.1, 0.5, 0.03); } },
  use: {
    len: 0.55, level: -17, bus: 'sfx', verb: 0.5, vary: 0.05, draw: k => {
      whoosh(k, 0, 0.38, 0.3, 3500, 500, -0.3, 0.5);
      tone(k, { at: 0, dur: 0.2, vol: 0.14, f: 760, to: 280, type: 'triangle' }); click(k, 0, 4000, 0.08);
    },
  },
  drop: {
    len: 0.6, level: -17, bus: 'sfx', verb: 0.35, vary: 0.06, draw: k => {
      thump(k, 0, 240, 70, 0.25, 0.6); noise(k, { at: 0, dur: 0.08, vol: 0.25, f: 900, attack: 0.001 }); click(k, 0, 2200, 0.15);
      for (const [d, p] of [[0.05, -0.3], [0.09, 0.3], [0.15, 0]]) metal(k, d, 1800, 0.08, 0.04, p); // the thing rattles as it lands
    },
  },
  fire: {
    len: 0.9, level: -15, bus: 'sfx', verb: 0.6, vary: 0.04, draw: k => {
      const s = saturate(k, 2.2);
      tone(s, { at: 0, dur: 0.35, vol: 0.22, f: 130, to: 880, type: 'sawtooth', lp: 600, lpTo: 5000, q: 4, detune: 12 });
      whoosh(k, 0, 0.45, 0.3, 600, 5500, -0.1, 0.6);
      noise(k, { at: 0.12, dur: 0.4, vol: 0.12, filter: 'bandpass', f: 5000, to: 900, q: 1.3, attack: 0.02, pan: 0.3 });
      thump(k, 0, 110, 45, 0.2, 0.4);
      click(k, 0, 3500, 0.2); click(k, 0.02, 5000, 0.12);
    },
  },
  boost: {
    len: 1.4, level: -14, bus: 'sfx', verb: 0.6, duck: 0.35, vary: 0.02, draw: k => {
      whoosh(k, 0, 0.95, 0.3, 300, 4500, -0.5, 0.2);
      whoosh(k, 0.04, 0.95, 0.26, 380, 5200, 0.5, -0.2);
      supersaw(k, { at: 0, dur: 0.9, vol: 0.14, f: 98, voices: 5, spread: 14, lp: 400, lpTo: 3200, attack: 0.02 });
      tone(k, { at: 0, dur: 0.9, vol: 0.12, f: 98, to: 392, type: 'sawtooth', lp: 500, lpTo: 2500, detune: 10 });
      thump(k, 0, 70, 120, 0.7, 0.35);
      sparkle(k, 0.35, 0.6, 0.04);
    },
  },
  charge: { len: 0.8, level: -19, bus: 'sfx', verb: 0.7, vary: 0.01, draw: k => [B4, D5, Fs5, B5].forEach((f, i) => string(k, i * 0.07, f, 0.4, 0.3, (i - 1.5) * 0.2)) },
  // ---- impacts: the player hit, someone else hit ----
  hurt: {
    len: 1.2, level: -12, bus: 'sfx', verb: 0.3, duck: 0.6, vary: 0.05, draw: k => {
      const s = saturate(k, 3);
      thump(s, 0, 170, 38, 0.45, 0.8);
      noise(s, { at: 0, dur: 0.4, vol: 0.45, f: 3000, to: 280, attack: 0.001 });
      metal(k, 0.005, 310, 0.5, 0.12, 0.2);
      tone(k, { at: 0, dur: 0.5, vol: 0.12, f: 420, to: 70, type: 'sawtooth', lp: 1400 });
      noise(k, { at: 0.06, dur: 0.35, vol: 0.12, filter: 'highpass', f: 2500, attack: 0.005, pan: -0.4 }); // grit scattering
      tinkle(k, 0.18, 0.03);
    },
  },
  hit: {
    len: 0.7, level: -17, bus: 'sfx', verb: 0.45, vary: 0.08, draw: k => {
      const s = saturate(k, 2);
      thump(s, 0, 150, 55, 0.18, 0.6); noise(s, { at: 0, dur: 0.18, vol: 0.35, filter: 'bandpass', f: 1600, to: 450, q: 0.9, attack: 0.001 });
      metal(k, 0, 520, 0.3, 0.07);
      metal(k, 0.12, 2900, 0.1, 0.02, 0.3);
    },
  },
  shield: {
    len: 1, level: -17, bus: 'sfx', verb: 1.1, vary: 0.03, draw: k => {
      fm(k, { at: 0, dur: 0.6, vol: 0.2, f: 1200, to: 800, ratio: 1.5, index: 1.2, detune: 10 });
      tone(k, { at: 0, dur: 0.4, vol: 0.06, f: 2400, to: 1600, type: 'sine', pan: 0.3 });
      noise(k, { at: 0, dur: 0.25, vol: 0.08, filter: 'highpass', f: 5000, attack: 0.001 });
      glass(k, 0.05, B5, 0.5, 0.06, -0.2);
    },
  },
  warn: {
    len: 0.5, level: -15, bus: 'sfx', verb: 0.2, vary: 0, draw: k => {
      for (const at of [0, 0.16]) { glass(k, at, A6, 0.25, 0.3); tone(k, { at, dur: 0.1, vol: 0.05, f: A5, type: 'sine', attack: 0.002 }); }
    },
  },
  storm: {
    len: 2.1, level: -15, bus: 'sfx', verb: 0.8, duck: 0.4, vary: 0.05, draw: k => {
      noise(k, { at: 0, dur: 1.7, vol: 0.4, f: 900, to: 140, attack: 0.04, pan: -0.25 });
      noise(k, { at: 0.03, dur: 1.6, vol: 0.35, f: 800, to: 120, attack: 0.05, pan: 0.25 });
      thump(k, 0.02, 75, 32, 1.2, 0.45);
      tone(k, { at: 0.05, dur: 1.4, vol: 0.18, f: 46, type: 'sine', attack: 0.05, hold: 0.5 }); // the rumble under it
      for (const [at, p] of [[0.02, -0.5], [0.09, 0.4], [0.17, 0.1], [0.31, -0.2]]) noise(k, { at, dur: 0.05, vol: 0.18, filter: 'highpass', f: 3000, attack: 0.001, pan: p }); // crackle
    },
  },
  // ---- race progress ----
  lap: { len: 1.1, level: -16, bus: 'sfx', verb: 0.8, vary: 0, draw: k => { ring(k, 0, A5, 0.35, 0.35); ring(k, 0.13, D6, 0.85, 0.35, 0, 0.65); glass(k, 0.13, A6, 0.3, 0.05); } },
  finish: {
    len: 2.9, level: -12, bus: 'sfx', verb: 1.2, duck: 0.55, vary: 0, draw: k => {
      [D5, Fs5, A5, D6].forEach((f, i) => string(k, i * 0.1, f, 0.5, 0.3, (i - 1.5) * 0.25));
      [A5, D6, Fs6].forEach((f, i) => ring(k, 0.42, f, 2, 0.22, (i - 1) * 0.45, 0.75));
      tone(k, { at: 0.42, dur: 1.8, vol: 0.14, f: D3, type: 'triangle', lp: 700, attack: 0.01, hold: 0.6 });
      thump(k, 0.42, 110, 45, 0.4, 0.45);
      sparkle(k, 0.46, 1.5, 0.05);
      roar(k, 0.3, 2.3, 0.07, 1800);
      [[0.55, -0.6], [0.68, 0.5], [0.79, -0.2], [0.9, 0.7], [1.04, -0.5], [1.15, 0.3]].forEach(([at, p]) => clap(k, at, 0.08, p));
    },
  },
  lose: { len: 1.5, level: -18, bus: 'sfx', verb: 0.8, vary: 0, draw: k => [A4, Fs4, D4].forEach((f, i) => string(k, i * 0.22, f, 0.7, 0.3, (i - 1) * 0.2, 0.3)) },
  elim: { len: 0.6, level: -19, bus: 'sfx', verb: 0.6, vary: 0.03, draw: k => { tone(k, { at: 0, dur: 0.32, vol: 0.2, f: 300, to: 150, type: 'triangle', lp: 1500 }); thump(k, 0.02, 120, 60, 0.3, 0.15); } },
  deny: { len: 0.35, level: -18, bus: 'ui', verb: 0.1, vary: 0, draw: k => { tap(k, 0, 160, 0.25); tap(k, 0.11, 148, 0.25); } },
  // ---- workouts ----
  interval: { len: 1, level: -15, bus: 'sfx', verb: 0.7, duck: 0.3, vary: 0, draw: k => { glass(k, 0, A5, 0.3, 0.3); glass(k, 0.14, E6, 0.7, 0.3); tone(k, { at: 0.14, dur: 0.3, vol: 0.03, f: E6 * 2, type: 'sine' }); } },
  tick: { len: 0.12, level: -22, bus: 'sfx', verb: 0.2, vary: 0, draw: k => { tone(k, { at: 0, dur: 0.05, vol: 0.3, f: 1500, type: 'sine', attack: 0.001 }); click(k, 0, 5000, 0.06); } },
  // ---- end screen and interface ----
  levelup: {
    len: 2.6, level: -14, bus: 'ui', verb: 1.2, vary: 0, draw: k => {
      [D5, Fs5, A5, D6, Fs6, A6].forEach((f, i) => ring(k, i * 0.08, f, 0.55, 0.16, (i - 2.5) * 0.15, 0.6));
      [D6, Fs6, A6].forEach((f, i) => ring(k, 0.5, f, 1.8, 0.16, (i - 1) * 0.4, 0.75));
      tone(k, { at: 0.5, dur: 1.6, vol: 0.12, f: D3, type: 'triangle', lp: 700, attack: 0.01, hold: 0.5 });
      sparkle(k, 0.45, 1.3, 0.05);
    },
  },
  // FTP up: a climb in fourths and fifths on a plucked string, a bright glass on top (lighter than the level-up)
  ftpup: {
    len: 1.9, level: -15, bus: 'ui', verb: 1.1, vary: 0, draw: k => {
      [A4, D5, Fs5, A5, D6].forEach((f, i) => string(k, i * 0.07, f, 0.5, 0.26, (i - 2) * 0.2));
      glass(k, 0.38, A6, 1.1, 0.14); sparkle(k, 0.35, 0.8, 0.04);
    },
  },
  click: { len: 0.07, level: -25, bus: 'ui', verb: 0, vary: 0.04, draw: k => { noise(k, { at: 0, dur: 0.012, vol: 0.3, filter: 'bandpass', f: 2800, q: 2, attack: 0.0006 }); tone(k, { at: 0, dur: 0.025, vol: 0.15, f: 1400, type: 'sine', attack: 0.0008 }); } },
  // ---- supporters ----
  cheer: {
    len: 1.2, level: -16, bus: 'sfx', verb: 0.9, vary: 0.04, draw: k => {
      roar(k, 0, 0.85, 0.16, 1700); clap(k, 0.1, 0.25, -0.4); clap(k, 0.24, 0.22, 0.5);
      glass(k, 0.05, A5, 0.3, 0.08); glass(k, 0.17, D6, 0.4, 0.08);
    },
  },
  cheerBoost: {
    len: 1.6, level: -14, bus: 'sfx', verb: 1, vary: 0.03, draw: k => {
      roar(k, 0, 1.15, 0.18, 2200); [0.08, 0.2, 0.31, 0.45].forEach((at, i) => clap(k, at, 0.22, i % 2 ? 0.5 : -0.5));
      [A5, D6, Fs6].forEach((f, i) => ring(k, i * 0.1, f, 0.5, 0.1, (i - 1) * 0.3)); glass(k, 0.3, A6, 0.6, 0.05);
    },
  },
  // ---- bike ----
  // derailleur: a sharp metallic click, the cage moving, then the chain settling on the next sprocket
  shift: {
    len: 0.18, level: -20, bus: 'sfx', verb: 0.1, vary: 0.06, draw: k => {
      noise(k, { at: 0, dur: 0.025, vol: 0.3, filter: 'bandpass', f: 3800, q: 3, attack: 0.0008 });
      metal(k, 0.002, 1900, 0.06, 0.05);
      noise(k, { at: 0.045, dur: 0.05, vol: 0.2, filter: 'bandpass', f: 2400, q: 2, attack: 0.001 });
      thump(k, 0.045, 300, 120, 0.04, 0.12);
    },
  },
  // ---- roadside crowd one-shots (riding sounds, placed by audio/ride.ts) ----
  clap: { len: 0.2, level: -24, bus: 'amb', verb: 0.6, vary: 0.08, draw: k => clap(k, 0, 0.4) },
  // cowbells on the climbs: a real bell's two modes, struck twice
  cowbell: {
    len: 0.5, level: -24, bus: 'amb', verb: 0.7, vary: 0.05, draw: k => {
      for (const at of [0, 0.16]) { metal(k, at, 560, 0.32, 0.25, 0, COWBELL); click(k, at, 4000, 0.08); }
    },
  },
  whistle: {
    len: 0.85, level: -26, bus: 'amb', verb: 0.8, vary: 0.06, draw: k => {
      tone(k, { at: 0, dur: 0.25, vol: 0.18, f: 2200, to: 3100, type: 'sine', attack: 0.02, detune: 5 });
      tone(k, { at: 0.25, dur: 0.5, vol: 0.18, f: 3100, to: 2000, type: 'sine', attack: 0.01, detune: 5 });
    },
  },
};

export const SOUND_NAMES = Object.keys(SOUNDS);

/** Loudest 50 ms window (RMS over both channels) and the sample peak of a rendered sound. */
export function measure(chans: Float32Array[], sampleRate: number): { rms: number; peak: number } {
  const win = Math.max(1, Math.floor(sampleRate * 0.05)), n = chans[0]?.length ?? 0;
  let peak = 0, best = 0;
  for (let s = 0; s < n; s += win >> 1) {
    let sum = 0, cnt = 0;
    for (const c of chans) for (let i = s; i < Math.min(n, s + win); i++) { const x = c[i]; sum += x * x; cnt++; if (Math.abs(x) > peak) peak = Math.abs(x); }
    if (cnt) best = Math.max(best, sum / cnt);
  }
  return { rms: Math.sqrt(best), peak };
}

/** Gain bringing a sound to its target loudness, without its peak going over the ceiling (dBFS). Silence stays 0. */
export function normGain(rms: number, peak: number, targetDb: number, ceilingDb = -1): number {
  if (!(rms > 1e-6) || !(peak > 0)) return 0;
  const want = Math.pow(10, targetDb / 20) / rms, cap = Math.pow(10, ceilingDb / 20) / peak;
  return Math.min(want, cap);
}

const ready = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer | null>>();

/** Renders one sound offline (stereo) and normalises it in place. */
async function render(name: string, sampleRate: number, noiseBuf: AudioBuffer): Promise<AudioBuffer | null> {
  const def = SOUNDS[name];
  if (!def || typeof OfflineAudioContext === 'undefined') return null;
  const ac = new OfflineAudioContext(2, Math.ceil(def.len * sampleRate), sampleRate);
  def.draw({ ac, out: ac.destination, noise: noiseBuf });
  const buf = await ac.startRendering();
  const chans = [buf.getChannelData(0), buf.getChannelData(1)], m = measure(chans, sampleRate), g = normGain(m.rms, m.peak, def.level);
  for (const c of chans) for (let i = 0; i < c.length; i++) c[i] *= g;
  ready.set(name, buf);
  return buf;
}

/** A sound's buffer if rendered. */
export const bankBuffer = (name: string): AudioBuffer | undefined => ready.get(name);

/** The buffer of a sound, rendering it if needed (shared promise: one render per sound). */
export function loadSound(name: string, sampleRate: number, noiseBuf: AudioBuffer): Promise<AudioBuffer | null> {
  const have = ready.get(name);
  if (have) return Promise.resolve(have);
  let p = pending.get(name);
  if (!p) { p = render(name, sampleRate, noiseBuf).catch(() => null); pending.set(name, p); }
  return p;
}

/** Renders the whole bank, one sound after another so the page never stalls (the countdown's first). */
export async function prepareBank(sampleRate: number, noiseBuf: AudioBuffer) {
  for (const n of ['beep', 'go', ...SOUND_NAMES]) await loadSound(n, sampleRate, noiseBuf);
}
